import { FC, useCallback, useMemo } from 'react';

import { Booking, BookingDetails, EquipmentType, SIZE_TYPE_COMBOS } from 'types';

import { SearchableSelectArray, SearchableSelectArrayOption, SearchableSelectArrayOptionGroup } from 'components';
import { useSizeTypes } from 'contexts';
import '../booking-details.scss';
import { useEditBookings } from './edit-booking.context';
import './edit-booking.scss';

const DetailRow: FC<{ details: BookingDetails; detailList: string[]; index: number }> = ({
  details,
  detailList,
  index
}) => {
  const { booking, setBooking } = useEditBookings();
  const { containerSizeTypeEntries, chassisSizeTypeEntries, gensetSizeTypeEntries, getEquipmentType } = useSizeTypes();

  /* booking accept-list dropdown — container group carries the FE-only combo overlay */
  const containerEntries = useMemo(
    () => [...containerSizeTypeEntries, ...SIZE_TYPE_COMBOS],
    [containerSizeTypeEntries]
  );

  const sizeTypeOptions: SearchableSelectArrayOptionGroup[] = useMemo(() => {
    const toOption = ([key, val]: [string, string[]]): SearchableSelectArrayOption => ({ value: val, label: key });
    const toGroup = (groupLabel: string, entries: [string, string[]][]): SearchableSelectArrayOptionGroup => ({
      groupLabel,
      value: entries
        .map(toOption)
        .filter(
          val =>
            val.value &&
            (!detailList.some(st => val.value?.includes(st)) || details.iso?.some(st => val.value?.includes(st)))
        )
    });
    return [
      toGroup('CONTAINER', containerEntries),
      toGroup('CHASSIS', chassisSizeTypeEntries),
      toGroup('GENSET', gensetSizeTypeEntries)
    ];
  }, [detailList, details.iso, containerEntries, chassisSizeTypeEntries, gensetSizeTypeEntries]);

  const handleChange = useCallback(
    (bd?: Partial<BookingDetails>) => {
      setBooking(b => {
        const bookingDetails: BookingDetails[] = Object.assign([], b?.bookingDetails);
        if (index in bookingDetails) {
          const [current] = bookingDetails.splice(index, 1);
          if (bd) bookingDetails.splice(index, 0, { ...current, ...bd });
          return { ...b, bookingDetails } as Booking;
        } else return b;
      });
    },
    [index, setBooking]
  );

  return (
    <div className="grid-7 p-2">
      <div className="col-span-2">
        <h6 className="text-info-dark">SIZE TYPE</h6>
        <div className="mt-2 w-100">
          <SearchableSelectArray
            onValueSelected={iso => handleChange({ iso })}
            options={sizeTypeOptions}
            value={
              [...chassisSizeTypeEntries, ...containerEntries, ...gensetSizeTypeEntries].find(
                st => st[1].every(iso => details.iso?.includes(iso)) && st[1].length === details.iso?.length
              )?.[0] ??
              /* retired/inactive code(s) have no active label — show the raw code(s) (zero-churn) */
              details.iso?.join('/')
            }
            required
          />
        </div>
      </div>
      <div>
        <h6 className="text-info-dark">QUANTITY</h6>
        <input
          type="number"
          className="mt-2 w-100"
          min={details.count ?? 0}
          onChange={e => handleChange({ count: Math.max(0, Number(e.target.value) || 0) })}
          value={details.count ?? ''}
          required
        />
      </div>
      <div>
        <h6 className="text-info-dark">RECEIVED</h6>
        <input type="number" className="mt-2 w-100" value={details.received ?? 0} disabled />
      </div>
      <div>
        <h6 className="text-info-dark">RESERVED</h6>
        <input type="number" className="mt-2 w-100" value={details.reserved ?? 0} disabled />
      </div>
      <div>
        <h6 className="text-info-dark">DELIVERED</h6>
        <input type="number" className="mt-2 w-100" value={details.delivered ?? 0} disabled />
      </div>
      <div className="grid-2 h-100">
        <div className="carb-checkbox">
          <h6 className="text-info-dark">CARB</h6>
          <div className="mt-2 p-2">
            <input
              className="w-100"
              type="checkbox"
              checked={!!details.carb}
              onChange={e => handleChange({ carb: e.target.checked })}
              disabled={
                !details.iso?.some(st => getEquipmentType(st) === EquipmentType.GENSET) &&
                !details.iso?.some(st => st.endsWith('RF'))
              }
            />
          </div>
        </div>
        <div className="align-self-end">
          <button
            className="btn-back p-2"
            type="button"
            onClick={() => handleChange()}
            disabled={(booking?.bookingDetails?.length ?? 0) <= 1 || !!details.delivered || !!details.reserved}
          >
            <span className="material-icons icon-xlarge">delete</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const EquipmentInformation: FC = () => {
  const { booking, setBooking } = useEditBookings();

  const handleAdd = useCallback(() => {
    setBooking(b => {
      if (!b) return b;
      const bookingDetails = Object.assign([], b.bookingDetails);
      bookingDetails.push({ carb: false, count: 1, iso: [] } as BookingDetails);
      return { ...b, bookingDetails };
    });
  }, [setBooking]);

  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>EQUIPMENT DETAILS</h4>
      </div>
      <div className="card-body">
        {booking?.bookingDetails?.map((bd, index) => (
          <DetailRow
            key={bd?.id ?? index}
            index={index}
            details={bd}
            detailList={booking?.bookingDetails?.map(bd => bd.iso ?? []).flatMap(detail => detail) ?? []}
          />
        ))}
        <div className="p-2">
          <button className="btn-outline" onClick={handleAdd}>
            ADD EQUIPMENT
          </button>
        </div>
      </div>
    </div>
  );
};

export default EquipmentInformation;
